import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index }) {
  const { addItem, setCartOpen } = useCart();
  const [size, setSize] = useState("M");
  const [color, setColor] = useState(product.colors?.[0] || "");
  const colors = product.colors || [];

  const handleAdd = () => {
    addItem(product, size, color);
    toast.success(
      `${product.name} (${size}${color ? `, ${color}` : ""}) aggiunto al carrello`,
      {
        action: { label: "Vai al carrello", onClick: () => setCartOpen(true) },
      }
    );
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      className="leather-card group overflow-hidden flex flex-col"
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 bg-[#0A0A0B]/85 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059] border border-[#C5A059]/30">
          {product.category}
        </span>
        {product.club_only && (
          <span
            data-testid={`club-badge-${product.id}`}
            className="absolute top-4 right-4 bg-[#9E1B1B] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4EBE1] border border-[#C5A059]/40"
          >
            Solo Soci
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-lg font-semibold text-[#F4EBE1] leading-snug">
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-[#C0B9B0] leading-relaxed flex-1">
          {product.description}
        </p>

        <div className="mt-4 flex items-center gap-2" data-testid={`size-picker-${product.id}`}>
          {product.sizes.map((s) => (
            <button
              key={s}
              data-testid={`size-${s}-${product.id}`}
              onClick={() => setSize(s)}
              className={`w-9 h-9 font-mono text-xs border transition-all duration-200 ${
                size === s
                  ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0B] font-bold"
                  : "border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059]/60"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {colors.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`color-picker-${product.id}`}>
            {colors.map((col) => (
              <button
                key={col}
                data-testid={`color-${col}-${product.id}`}
                onClick={() => setColor(col)}
                className={`px-3 h-8 font-mono text-[10px] uppercase tracking-[0.1em] border transition-all duration-200 ${
                  color === col
                    ? "border-[#9E1B1B] bg-[#9E1B1B] text-[#F4EBE1] font-bold"
                    : "border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059]/60"
                }`}
              >
                {col}
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <span
            data-testid={`price-${product.id}`}
            className="font-display text-2xl font-bold text-[#C5A059]"
          >
            € {product.price.toFixed(2)}
          </span>
          <button
            data-testid={`add-to-cart-button-${product.id}`}
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#9E1B1B] hover:bg-[#C5A059] text-[#F4EBE1] hover:text-[#0A0A0B] font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300"
          >
            <ShoppingBag className="w-4 h-4" />
            Aggiungi
          </button>
        </div>
      </div>
    </motion.article>
  );
}
